"use client";

import {useCallback, useEffect, useRef, useState} from "react";
import {ArrowLeft, ArrowRight, X} from "lucide-react";
const CARDS = [
    {
        title: "Zobliss gives you all the features you need to grow at no additional cost.",
        note: "Boulevard, Mangomint, Mindbody, Square, and Vagaro don’t.",
    },
    {
        title: "Zobliss doesn’t jeopardize retention by showing clients your competitors.",
        note: "Mindbody and Vagaro do.",
    },
    {
        title: "Zobliss offers fast, login-free online booking that reduces drop-off.",
        note: "Boulevard, Mindbody, Square, and Vagaro don’t.",
    },
    {
        title: "Zobliss helps you save money with a 2.4% + $0.15 processing rate and no hidden fees.",
        note: "Boulevard, Mangomint, Mindbody, Square, and Vagaro don’t.",
    },
    {
        title: "Zobliss has features your staff can easily set up and understand from day one.",
        note: "Boulevard, Mangomint, Mindbody, Square, and Vagaro don’t.",
    },
];

// Horizontally scrolling "Zobliss vs others" cards. The track starts aligned with the
// max-w-7xl content column and bleeds off the right edge of the screen.
export default function BenefitComparison() {
    const trackRef = useRef(null);
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(true);

    const updateButtons = useCallback(() => {
        const track = trackRef.current;
        if (!track) {
            return;
        }
        setCanPrev(track.scrollLeft > 4);
        setCanNext(track.scrollLeft + track.clientWidth < track.scrollWidth - 4);
    }, []);

    useEffect(() => {
        updateButtons();
        window.addEventListener("resize", updateButtons);
        return () => window.removeEventListener("resize", updateButtons);
    }, [updateButtons]);

    const scrollByCard = (direction) => {
        const track = trackRef.current;
        const card = track?.querySelector("[data-card]");
        if (!track || !card) {
            return;
        }
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        track.scrollBy({left: direction * (card.offsetWidth + gap), behavior: "smooth"});
    };

    return (
        <section className="bg-[#eef0f0] py-16 md:py-24 overflow-hidden">
            <div className="max-w-7xl mx-auto px-6 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
                <div>
                    <p className="text-sm font-medium text-neutral-900">Benefit Comparison</p>
                    <h2 className="mt-4 text-4xl sm:text-6xl font-medium tracking-tight text-neutral-900 text-pretty max-w-2xl">
                        Zobliss helps you grow. Others don’t.
                    </h2>
                </div>
                <div className="flex gap-4 shrink-0">
                    <button
                        type="button"
                        onClick={() => scrollByCard(-1)}
                        disabled={!canPrev}
                        aria-label="Previous"
                        className="flex size-12 items-center justify-center rounded-lg bg-white text-neutral-900 shadow-sm transition cursor-pointer hover:bg-neutral-100 disabled:cursor-default disabled:bg-white/60 disabled:text-neutral-400"
                    >
                        <ArrowLeft className="size-5"/>
                    </button>
                    <button
                        type="button"
                        onClick={() => scrollByCard(1)}
                        disabled={!canNext}
                        aria-label="Next"
                        className="flex size-12 items-center justify-center rounded-lg bg-white text-neutral-900 shadow-sm transition cursor-pointer hover:bg-neutral-100 disabled:cursor-default disabled:bg-white/60 disabled:text-neutral-400"
                    >
                        <ArrowRight className="size-5"/>
                    </button>
                </div>
            </div>

            <div
                ref={trackRef}
                onScroll={updateButtons}
                className="mt-10 md:mt-12 flex gap-4 overflow-x-auto snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden px-6 scroll-px-6 xl:px-[calc((100%-80rem)/2+1.5rem)] xl:scroll-px-[calc((100%-80rem)/2+1.5rem)]"
            >
                {CARDS.map((card) => (
                    <div
                        key={card.title}
                        data-card
                        className="snap-start shrink-0 w-[85vw] sm:w-[300px] md:w-[330px] h-[380px] md:h-[420px] rounded-2xl p-5 flex flex-col justify-between bg-[#11110d] text-white"
                    >
                        <h3 className="text-2xl md:text-[1.75rem] leading-tight font-normal text-pretty">
                            {card.title}
                        </h3>
                        <div className="flex items-center gap-3">
                            <span className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-white/10">
                                <X className="size-5" aria-hidden="true"/>
                            </span>
                            <p className="text-sm leading-snug">{card.note}</p>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}
